import React, { useMemo, useRef, useState } from "react";
import {
  Drawer,
  IconButton,
  TextField,
  Typography,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Menu,
  MenuItem,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CampaignIcon from "@mui/icons-material/Campaign";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import RuleIcon from "@mui/icons-material/Rule";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import SyncIcon from "@mui/icons-material/Sync";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BoltIcon from "@mui/icons-material/Bolt";
import SendIcon from "@mui/icons-material/Send";
import GroupsIcon from "@mui/icons-material/Groups";
import InsightsIcon from "@mui/icons-material/Insights";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// ---- Types ----
type PDFLink = { title: string; href: string };
type StepBlock = { title: string; bullets: string[]; source?: string };
type LimitRow = { component: string; limit: string };
type SyncRow = { action: string; machadalo: string; sigmo: string; sentToCustomer: string };
type NumberRow = { number: string; channel: string; account: string };

type TemplateHelpProps = {
  pdfs?: PDFLink[];
};

const DEFAULT_PDFS = [
  {
    title: "BOT BUILDER Help file (S3)",
    href: "https://mdimages-test.s3.amazonaws.com/ff782dbf-95d3-4d35-8e8d-0016891e138e_Template_Help.pdf",
  },
];
// ---- Static content (sourced from the Help Manual) ----

const CATEGORY_CARDS = [
  {
    title: "Marketing",
    icon: <CampaignIcon />,
    color: "#6366F1",
    desc: "Promotional and awareness messages — offers, launches, campaigns. Recipient need not be an existing customer. Highest per-message cost.",
    bullets: [
      "Promotional offers and discount campaigns",
      "New product / model launch announcements",
      "Event invites and webinar reminders",
      "Re-engagement broadcasts to cold leads",
    ],
  },
  {
    title: "Utility",
    icon: <BuildCircleIcon />,
    color: "#0EA5E9",
    desc: "Service or transactional messages for existing customers — confirmations, reminders, status updates. Lower cost than Marketing.",
    bullets: [
      "Booking or appointment confirmations",
      "Service due reminders",
      "Order status or delivery updates",
      "Payment receipts and invoices",
    ],
  },
  {
    title: "Authentication",
    icon: <VerifiedUserIcon />,
    color: "#10B981",
    desc: "Used only for OTPs and identity-verification codes. Select this category when the template's sole purpose is verification.",
    bullets: ["One-time passwords (OTPs)", "Login / transaction verification codes"],
  },
];

const ANATOMY_ROWS: LimitRow[] = [
  { component: "Header (optional)", limit: "Text (max 60 chars), Image, Video, or Document" },
  { component: "Body (required)", limit: "Max 1,024 characters · supports {{1}}, {{2}} variables" },
  { component: "Footer (optional)", limit: "Max 60 characters · commonly used for opt-out lines" },
  { component: "Buttons (optional)", limit: "Up to 3 · Quick Reply or Call-to-Action (URL / phone)" },
];

const HARD_LIMITS: LimitRow[] = [
  { component: "Body text", limit: "Max 1,024 characters" },
  { component: "Header text", limit: "Max 60 characters" },
  { component: "Footer text", limit: "Max 60 characters" },
  { component: "Buttons per template", limit: "Max 3" },
  { component: "Quick Reply button label", limit: "Max 25 characters" },
  { component: "Call-to-Action buttons", limit: "Max 2 (1 URL + 1 phone, or 2 URLs)" },
  { component: "Variables in body", limit: "No hard limit — each needs a sample value" },
  { component: "Image in header", limit: "Max 5 MB, JPEG or PNG only" },
  { component: "Video in header", limit: "Max 16 MB, MP4 only" },
  { component: "Document in header", limit: "Max 100 MB, PDF preferred" },
  { component: "Template submissions / day", limit: "Typically ~250 (varies by WABA tier)" },
];

const APPROVAL_STEPS: StepBlock[] = [
  { title: "Draft the template", bullets: ["Go to WhatsApp → Templates and click Add to open the builder."] },
  { title: "Assign category", bullets: ["Select MARKETING, UTILITY, or AUTHENTICATION."] },
  { title: "Fill sample values", bullets: ["Provide realistic sample values for every {{N}} placeholder."] },
  { title: "Submit for review", bullets: ["Click Save. Standard turnaround: 5 minutes to 1 hour."] },
  { title: "On Approved", bullets: ["Status changes to Approved; template becomes available for use."] },
  { title: "On Rejected", bullets: ["Review the rejection reason, fix content, resubmit under a new name."] },
];

const REJECTION_REASONS = [
  "Promotional language inside a UTILITY template — Meta may auto-convert it to MARKETING.",
  "Vague or missing sample variable values.",
  "Template name reused after a prior rejection — always use a new name.",
  "URL button domain not whitelisted on Meta Business Manager.",
  "Content flagged as spam, abusive, or policy-violating.",
];

const CREATE_STEPS: StepBlock[] = [
  { title: "Go to WhatsApp → Templates", bullets: ["Click Templates in the WhatsApp section of the left navigation."] },
  { title: "View the Template Dashboard", bullets: ["Shows all templates with category, language, and approval status."] },
  { title: "Click Add", bullets: ["Opens the template builder form."] },
  { title: "Select Category", bullets: ["Choose Marketing, Utility, or Authentication."] },
  { title: "Add Name & Description", bullets: ["Follow the naming convention; description is optional."] },
  { title: "Select Language", bullets: ["Pick the language of the message content, e.g. English."] },
  { title: "Add Header (optional)", bullets: ["Choose Text, Image, Video, or Document and provide the content."] },
  { title: "Write the Body", bullets: ["Use {{1}}, {{2}}… for dynamic fields. Provide realistic sample values."] },
  { title: "Add Footer (optional)", bullets: ["A short closing line, opt-out note, or instruction."] },
  { title: "Add Buttons (optional)", bullets: ["Call-to-Action (URL / phone) or Quick Reply buttons."] },
  { title: "Save the Template", bullets: ["Click Save — submits the template to Meta for review."] },
  { title: "Wait for Approval", bullets: ["Initial status is Pending; usually Approved within 1 hour."] },
];

const SYNC_ROWS: SyncRow[] = [
  { action: "Created in Machadalo (Sigmo number)", machadalo: "Yes", sigmo: "Yes — auto-reflected", sentToCustomer: "Yes" },
  { action: "Created directly in Sigmo (Sigmo number)", machadalo: "No", sigmo: "Yes", sentToCustomer: "Yes" },
  { action: "Edited in Machadalo (Direct WA number)", machadalo: "Yes", sigmo: "N/A", sentToCustomer: "Yes" },
  { action: "Edited in Sigmo (Sigmo number)", machadalo: "No — shows old version", sigmo: "Yes — updated", sentToCustomer: "Yes — latest version" },
];

const NUMBERS: NumberRow[] = [
  { number: "+91 98930 66045", channel: "Direct WhatsApp", account: "Machadalo" },
  { number: "+91 72088 80204", channel: "Direct WhatsApp", account: "Machadalo" },
  { number: "+91 72088 80209", channel: "Direct WhatsApp", account: "Machadalo" },
  { number: "+91 98930 66025", channel: "TrustSignal (Sigmo)", account: "Machadalo (main)" },
  { number: "+91 96650 32544", channel: "TrustSignal (Sigmo)", account: "Mercedes-Benz Live" },
  { number: "+91 96650 32309", channel: "TrustSignal (Sigmo)", account: "Mercedes-Benz Live" },
  { number: "+91 96650 32140", channel: "TrustSignal (Sigmo)", account: "Mercedes-Benz Test" },
  { number: "+91 96650 32142", channel: "TrustSignal (Sigmo)", account: "Mercedes-Benz Test" },
  { number: "+91 88269 98005", channel: "TrustSignal (Sigmo)", account: "Sharda" },
];

const SEND_ONE_TO_ONE: StepBlock[] = [
  { title: "Open template actions", bullets: ["Click the 3-dot menu in the Action column of the template row."] },
  { title: "Click Send", bullets: ["Opens the Send popup."] },
  { title: "Fill in details", bullets: ["Enter recipient's phone number and all variable values. A different header media file can optionally be uploaded here."] },
  { title: "Click Send", bullets: ["Delivery depends on the recipient's WhatsApp status and Meta rules."] },
];

const SEND_BULK: StepBlock[] = [
  { title: "Open template actions", bullets: ["Click the 3-dot menu in the Action column."] },
  { title: "Click 'Upload Sheet for Bulk Send'", bullets: ["Opens the bulk send dialog."] },
  { title: "Choose the Excel file", bullets: ["Select the file with recipient data and variable values."] },
  { title: "Map parameters", bullets: ["Map each template variable to the matching column in the file."] },
  { title: "Preview the data", bullets: ["Review the final message preview before dispatch."] },
  { title: "Verify and Send", bullets: ["Confirm the data, then click Send to dispatch the bulk campaign."] },
];

const Pill: React.FC<{ label: string; color?: string }> = ({ label, color }) => (
  <Chip
    size="small"
    label={label}
    sx={{ borderRadius: 2, ...(color ? { bgcolor: `${color}20`, color, fontWeight: 600 } : {}) }}
  />
);

const SectionHeader: React.FC<{ title: string; subtitle?: string; id?: string }> = ({ title, subtitle, id }) => (
  <Box id={id} sx={{ mb: 1.25, mt: 3 }}>
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
);

const LimitTable: React.FC<{ rows: LimitRow[]; colA?: string; colB?: string }> = ({
  rows,
  colA = "Component",
  colB = "Limit",
}) => (
  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: "action.hover" }}>
          <TableCell sx={{ fontWeight: 700 }}>{colA}</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>{colB}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.component}>
            <TableCell>{r.component}</TableCell>
            <TableCell>{r.limit}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const StepAccordion: React.FC<{ steps: StepBlock[]; label: string; defaultExpanded?: boolean }> = ({
  steps,
  label,
  defaultExpanded,
}) => {
  const [active, setActive] = useState(0);
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ mb: 2, borderRadius: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>
          {label} ({steps.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stepper activeStep={active} alternativeLabel sx={{ mb: 2, flexWrap: "wrap" }}>
          {steps.map((s, i) => (
            <Step key={s.title} onClick={() => setActive(i)}>
              <StepLabel>{s.title}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {steps.map((s, idx) => {
          const isActive = idx === active;
          return (
            <Box
              key={s.title}
              sx={{
                mb: 1.25,
                p: 1.25,
                border: (t) => `1px dashed ${t.palette.divider}`,
                borderRadius: 2,
                bgcolor: (t) => (isActive ? t.palette.action.hover : "transparent"),
              }}
            >
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {idx + 1}. {s.title}
              </Typography>
              <List dense sx={{ pt: 0 }}>
                {s.bullets.map((b, j) => (
                  <ListItem key={j} sx={{ py: 0.2 }}>
                    <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={`• ${b}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        })}
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setActive((p) => Math.max(0, p - 1))}>
            Previous
          </Button>
          <Button variant="contained" onClick={() => setActive((p) => Math.min(steps.length - 1, p + 1))}>
            Next
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const TemplateHelp: React.FC<TemplateHelpProps> = ({ pdfs }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const jumpTo = (hash: string) => {
    const el = contentRef.current?.querySelector(hash);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setMenuEl(e.currentTarget);
  const handleCloseMenu = () => setMenuEl(null);
  const handlePrint = () => {
    window.open(
      "https://mdimages-test.s3.amazonaws.com/ff782dbf-95d3-4d35-8e8d-0016891e138e_Template_Help.pdf",
      "_blank"
    );
  };

  const visiblePdfs = pdfs && pdfs.length > 0 ? pdfs : DEFAULT_PDFS;

  // Build a full searchable text blob per section directly from the real content
  // (instead of a hand-picked keyword list), so any word that actually appears
  // in a section will correctly surface it.
  const sectionTexts = useMemo(() => {
    const flatten = (steps: StepBlock[]) => steps.map((s) => `${s.title} ${s.bullets.join(" ")}`).join(" ");
    const flattenRows = (rows: LimitRow[]) => rows.map((r) => `${r.component} ${r.limit}`).join(" ");

    return {
      overview:
        "what are template messages overview pre-written meta-approved formats outside 24-hour session window bulk broadcast approval spam compliance whatsapp business policy",
      categories:
        "template categories " +
        CATEGORY_CARDS.map((c) => `${c.title} ${c.desc} ${c.bullets.join(" ")}`).join(" "),
      anatomy:
        "template anatomy variables header body footer buttons " +
        flattenRows(ANATOMY_ROWS) +
        " variable syntax positional placeholders sample values rahul honda city test drive confirmed",
      limits: "template hard limits meta platform rules " + flattenRows(HARD_LIMITS),
      naming:
        "naming convention client campaign type version lowercase underscores spaces hyphens special characters mb_ownership_plans_mktg_v1 essar_uco_onboarding_util_v2 kisan_advisory_util_v1",
      approval:
        "template approval process pending approved rejected review " +
        flatten(APPROVAL_STEPS) +
        " " +
        REJECTION_REASONS.join(" ") +
        " meta does not allow editing approved templates new versioned template",
      sync:
        "template management machadalo sigmo trustsignal sync rules sub-account edit rules runtime media replacement once per day " +
        SYNC_ROWS.map((r) => `${r.action} ${r.machadalo} ${r.sigmo} ${r.sentToCustomer}`).join(" "),
      create: "how to create a template builder " + flatten(CREATE_STEPS),
      triggers:
        "template triggers lead dashboard status field name case sensitive date time progress how triggers work",
      send:
        "how to send a template one-to-one bulk excel sheet map parameters preview verify send " +
        flatten(SEND_ONE_TO_ONE) +
        " " +
        flatten(SEND_BULK) +
        " runtime media replacement not supported for bulk sends",
      analytics:
        "template analytics delivery performance unique users sent percent sent delivered read logs view download export reporting",
      numbers:
        "numbers reference phone channel sub-account direct whatsapp trustsignal sigmo " +
        NUMBERS.map((n) => `${n.number} ${n.channel} ${n.account}`).join(" "),
    };
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (sectionKey: keyof typeof sectionTexts) =>
    !q || sectionTexts[sectionKey].toLowerCase().includes(q);

  return (
    <div>
      <Tooltip title="Help">
        <IconButton onClick={() => setIsOpen(true)}>
          <HelpOutlineIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)} sx={{ zIndex: 1400 }}>
        <Box
          sx={{
            width: 560,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: (t) => (t.palette.mode === "light" ? "#fff" : "background.paper"),
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "linear-gradient(90deg, rgba(16,185,129,0.16), rgba(14,165,233,0.16))",
              backdropFilter: "saturate(1.2)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <MenuBookIcon />
              <Typography variant="h6" fontWeight={800}>
                Template Messages • Help
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              {/* <Tooltip title="Download manual">
                <IconButton onClick={handleOpenMenu}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip> */}
              <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={handleCloseMenu}>
                {visiblePdfs.map((p) => (
                  <MenuItem
                    key={p.title}
                    component={p.href ? "a" : "li"}
                    href={p.href || undefined}
                    target={p.href ? "_blank" : undefined}
                    rel={p.href ? "noopener noreferrer" : undefined}
                    onClick={handleCloseMenu}
                  >
                    <PictureAsPdfIcon fontSize="small" style={{ marginRight: 8 }} />
                    {p.title}
                  </MenuItem>
                ))}
              </Menu>
              <Tooltip title="Print / Save as PDF">
                <IconButton onClick={handlePrint}>
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Search + Quick Jump */}
          <Box sx={{ px: 2, pt: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search categories, limits, steps, triggers…"
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              <Chip label="Categories" size="small" variant="outlined" onClick={() => jumpTo("#sec-categories")} sx={{ mb: 1 }} />
              <Chip label="Anatomy" size="small" variant="outlined" onClick={() => jumpTo("#sec-anatomy")} sx={{ mb: 1 }} />
              <Chip label="Limits" size="small" variant="outlined" onClick={() => jumpTo("#sec-limits")} sx={{ mb: 1 }} />
              <Chip label="Naming" size="small" variant="outlined" onClick={() => jumpTo("#sec-naming")} sx={{ mb: 1 }} />
              <Chip label="Approval" size="small" variant="outlined" onClick={() => jumpTo("#sec-approval")} sx={{ mb: 1 }} />
              <Chip label="Sigmo Sync" size="small" variant="outlined" onClick={() => jumpTo("#sec-sync")} sx={{ mb: 1 }} />
              <Chip label="Create" size="small" variant="outlined" onClick={() => jumpTo("#sec-create")} sx={{ mb: 1 }} />
              <Chip label="Triggers" size="small" variant="outlined" onClick={() => jumpTo("#sec-triggers")} sx={{ mb: 1 }} />
              <Chip label="Send" size="small" variant="outlined" onClick={() => jumpTo("#sec-send")} sx={{ mb: 1 }} />
              <Chip label="Analytics" size="small" variant="outlined" onClick={() => jumpTo("#sec-analytics")} sx={{ mb: 1 }} />
              <Chip label="Numbers" size="small" variant="outlined" onClick={() => jumpTo("#sec-numbers")} sx={{ mb: 1 }} />
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Scrollable Content */}
          <Box ref={contentRef} sx={{ px: 2, pb: 3, overflowY: "auto" }}>
            {/* Overview */}
            {matches("overview") && (
              <>
                <SectionHeader title="What Are Template Messages?" subtitle="Pre-written, Meta-approved formats used outside the 24-hour session window." />
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Template Messages are the only way to start a conversation outside the 24-hour session window, or to
                  send bulk / broadcast messages to multiple recipients at once.
                </Typography>
                <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 1 }}>
                  Meta requires every template to be approved before use, to prevent spam and keep messages compliant
                  with WhatsApp Business Policy. Once approved, the text is locked — any change needs a fresh submission.
                </Alert>
              </>
            )}

            {/* Categories */}
            {matches("categories") && (
              <>
                <SectionHeader id="sec-categories" title="Template Categories" subtitle="Pick the category that matches the message's purpose." />
                <Grid container spacing={1.5} sx={{ mb: 1 }}>
                  {CATEGORY_CARDS.map((c) => (
                    <Grid item xs={12} key={c.title}>
                      <Card variant="outlined" sx={{ borderRadius: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.2,
                            px: 2,
                            py: 1,
                            borderBottom: (t) => `1px solid ${t.palette.divider}`,
                            bgcolor: `${c.color}14`,
                          }}
                        >
                          <Box sx={{ color: c.color, display: "grid", placeItems: "center" }}>{c.icon}</Box>
                          <Typography fontWeight={700}>{c.title}</Typography>
                        </Box>
                        <CardContent sx={{ pt: 1.25 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {c.desc}
                          </Typography>
                          <List dense sx={{ pt: 0 }}>
                            {c.bullets.map((b, i) => (
                              <ListItem key={i} sx={{ py: 0.2 }}>
                                <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={`• ${b}`} />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {/* Anatomy & Variables */}
            {matches("anatomy") && (
              <>
                <SectionHeader id="sec-anatomy" title="Template Anatomy & Variables" subtitle="Every template is built from the same components." />
                <LimitTable rows={ANATOMY_ROWS} colA="Component" colB="Details" />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Variable syntax
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Use positional placeholders in the Body (and text Header): {"{{1}}, {{2}}, {{3}}…"}
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: "italic", mb: 0.5 }}>
                    "Hello {"{{1}}"}, your test drive for {"{{2}}"} is confirmed on {"{{3}}"} at {"{{4}}"}."
                  </Typography>
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Always give realistic sample values when submitting for approval — vague samples like "name" or
                    "value" are the #1 rejection cause. Use real-looking examples: "Rahul", "Honda City", "15 June 2025".
                  </Alert>
                </Card>
              </>
            )}

            {/* Hard limits */}
            {matches("limits") && (
              <>
                <SectionHeader id="sec-limits" title="Template Hard Limits" subtitle="Meta platform rules — exceeding these blocks submission." />
                <LimitTable rows={HARD_LIMITS} />
              </>
            )}

            {/* Naming convention */}
            {matches("naming") && (
              <>
                <SectionHeader id="sec-naming" title="Naming Convention" subtitle="Keep templates organised across clients and campaigns." />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <DriveFileRenameOutlineIcon fontSize="small" />
                    <Typography fontWeight={600}>[client]_[campaign]_[type]_[version]</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mb: 1 }}>
                    <Pill label="mb_ownership_plans_mktg_v1" />
                    <Pill label="essar_uco_onboarding_util_v2" />
                    <Pill label="kisan_advisory_util_v1" />
                  </Stack>
                  <List dense sx={{ pt: 0 }}>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="• All lowercase, underscores only — no spaces, hyphens, or special characters." />
                    </ListItem>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="• Max 512 characters (keep it under 60 in practice)." />
                    </ListItem>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="• Always increment the version when resubmitting a modified template." />
                    </ListItem>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="• Names are permanent once approved — plan the structure before the first submission." />
                    </ListItem>
                  </List>
                </Card>
              </>
            )}

            {/* Approval process */}
            {matches("approval") && (
              <>
                <SectionHeader id="sec-approval" title="Template Approval Process" subtitle="Typical turnaround: 5 minutes to 1 hour." />
                <StepAccordion steps={APPROVAL_STEPS} label="Approval steps" />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <RuleIcon fontSize="small" />
                    <Typography fontWeight={600}>Common rejection reasons</Typography>
                  </Stack>
                  <List dense sx={{ pt: 0 }}>
                    {REJECTION_REASONS.map((r, i) => (
                      <ListItem key={i} sx={{ py: 0.2 }}>
                        <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={`${i + 1}. ${r}`} />
                      </ListItem>
                    ))}
                  </List>
                </Card>
                <Alert severity="error" icon={<FactCheckIcon fontSize="small" />} sx={{ mb: 1 }}>
                  Meta does not allow editing of approved templates. Create a new versioned template (e.g.
                  mb_test_drive_v2) instead — the old one stays active until paused or deleted.
                </Alert>
              </>
            )}

            {/* Sigmo sync */}
            {matches("sync") && (
              <>
                <SectionHeader id="sec-sync" title="Template Management — Machadalo ↔ Sigmo" subtitle="Sync behaviour depends on which channel a number belongs to." />
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 1, overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Machadalo</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Sigmo</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Sent to customer?</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {SYNC_ROWS.map((r) => (
                        <TableRow key={r.action}>
                          <TableCell>{r.action}</TableCell>
                          <TableCell>{r.machadalo}</TableCell>
                          <TableCell>{r.sigmo}</TableCell>
                          <TableCell>{r.sentToCustomer}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Alert severity="info" icon={<SyncIcon fontSize="small" />} sx={{ mb: 1 }}>
                  Templates edited in Sigmo will NOT update inside Machadalo — Machadalo keeps showing the old version.
                  But the latest Sigmo-edited version is still what gets delivered to the customer. Always edit
                  Sigmo-linked templates inside Sigmo, and edit Direct WhatsApp templates inside Machadalo. Templates
                  can be edited once per day.
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  If a header is a media file and the template is already approved, a different file can be sent at
                  runtime without re-approval — but only for one-to-one sends, never for bulk sends.
                </Typography>
              </>
            )}

            {/* Create steps */}
            {matches("create") && (
              <>
                <SectionHeader id="sec-create" title="How to Create a Template" subtitle="Same flow for every template type." />
                <StepAccordion steps={CREATE_STEPS} label="Creation steps" defaultExpanded />
              </>
            )}

            {/* Triggers */}
            {matches("triggers") && (
              <>
                <SectionHeader id="sec-triggers" title="Template Triggers" subtitle="Auto-send a template when a lead's status changes." />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <BoltIcon fontSize="small" />
                    <Typography fontWeight={600}>How triggers work</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    When a lead's status changes, the system checks for any trigger configured on that status and
                    dispatches the linked template, optionally carrying live field values from the dashboard.
                  </Typography>
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Parameter names must exactly match the Field Name in the lead dashboard — matching is
                    case-sensitive ('Date' ≠ 'date').
                  </Alert>
                  <Typography variant="caption" color="text.secondary">
                    Example: dashboard fields Date, Time, Progress → template must use {"{{Date}}, {{Time}}, {{Progress}}"}.
                  </Typography>
                </Card>
              </>
            )}

            {/* Send */}
            {matches("send") && (
              <>
                <SectionHeader id="sec-send" title="How to Send a Template" subtitle="One-to-one or bulk, via an uploaded sheet." />
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <SendIcon fontSize="small" />
                  <Typography fontWeight={600}>One-to-one sending</Typography>
                </Stack>
                <StepAccordion steps={SEND_ONE_TO_ONE} label="One-to-one steps" />
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <GroupsIcon fontSize="small" />
                  <Typography fontWeight={600}>Bulk sending</Typography>
                </Stack>
                <StepAccordion steps={SEND_BULK} label="Bulk send steps" />
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Runtime media replacement isn't supported for bulk sends — confirm the correct header media is part
                  of the approved template before running a bulk campaign.
                </Alert>
              </>
            )}

            {/* Analytics */}
            {matches("analytics") && (
              <>
                <SectionHeader id="sec-analytics" title="Template Analytics" subtitle="Delivery performance per template, broken down by date." />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <InsightsIcon fontSize="small" />
                    <Typography fontWeight={600}>What you can see</Typography>
                  </Stack>
                  <List dense sx={{ pt: 0 }}>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="• Click a template row to see Unique Users Sent, % Sent, % Delivered, % Read by date." />
                    </ListItem>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="• Click View to see the full log of every send attempt." />
                    </ListItem>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="• Click Download to export logs for offline analysis or reporting." />
                    </ListItem>
                  </List>
                </Card>
              </>
            )}

            {/* Numbers reference */}
            {matches("numbers") && (
              <>
                <SectionHeader id="sec-numbers" title="Numbers Reference" subtitle="Which channel and sub-account each number belongs to." />
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Number</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Channel</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Sub-Account</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {NUMBERS.map((n) => (
                        <TableRow key={n.number}>
                          <TableCell>{n.number}</TableCell>
                          <TableCell>{n.channel}</TableCell>
                          <TableCell>{n.account}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Footer */}
            <Box sx={{ mt: 2, px: 1.5, py: 1, borderTop: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Tip: Use the Download icon in the header to fetch the full Help Manual PDF, or print this page to PDF.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </div>
  );
};

export default TemplateHelp;